<template>
  <section class="newcomers-section" id="join-us">
    <div class="section-container">
      <div class="section-header">
        <h2 class="section-title">Ready to Join The Players League?</h2>
        <p class="section-subtitle">Start your journey in competitive pool today</p>
      </div>

      <div class="content-card">
        <div class="welcome-content">
          <div class="intro-text">
            <Icon name="material-symbols:pool" class="section-icon" />
            <p class="main-description">
              Whether you're an experienced player looking for a competitive team environment or new to league play, 
              we're excited to welcome you! The Players League DFW offers a fun and engaging way to enjoy pool, 
              meet new people, and test your skills.
            </p>
          </div>

          <div class="getting-started">
            <h3 class="subsection-title">
              <Icon name="material-symbols:rocket-launch" />
              How to Get Started
            </h3>
            <div class="options-grid">
              <div class="option-card">
                <div class="option-icon">
                  <Icon name="material-symbols:group" />
                </div>
                <div class="option-content">
                  <h4>Form Your Team</h4>
                  <p>Bring your friends or colleagues and create your own competitive team</p>
                </div>
              </div>
              <div class="option-card">
                <div class="option-icon">
                  <Icon name="material-symbols:person-add" />
                </div>
                <div class="option-content">
                  <h4>Individual Players</h4>
                  <p>Looking for a team? We can help place individual players with existing teams</p>
                </div>
              </div>
            </div>
          </div>

          <div class="contact-section">
            <p class="contact-text">
              For all inquiries about joining, league rules, or any other questions, please don't hesitate to reach out.
            </p>
            <div class="cta-container">
              <NuxtLink to="/contact" class="cta-button">
                <Icon name="material-symbols:mail" />
                Contact Us to Join
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
</script>

<style scoped>
.newcomers-section {
  padding: 80px 0;
  background: 
    linear-gradient(180deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 58, 138, 0.9) 100%),
    url('/images/billiards-2-1920w.webp');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.newcomers-section::before {
  content: '';
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(255, 184, 28, 0.08) 0%, transparent 60%),
    radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.08) 0%, transparent 60%);
  z-index: 1;
}

.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-title {
  color: var(--gold);
  font-size: 3rem;
  font-family: var(--font-heading);
  font-weight: 700;
  margin-bottom: 16px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  border-radius: 2px;
}

.section-subtitle {
  color: var(--cream);
  font-size: 1.2rem;
  opacity: 0.9;
  margin-top: 24px;
}

.content-card {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 48px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  max-width: 900px;
  margin: 0 auto;
}

.content-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 184, 28, 0.05), transparent);
  transition: left 0.8s ease;
}

.content-card:hover::before {
  left: 100%;
}

.welcome-content {
  color: var(--cream);
}

.intro-text {
  text-align: center;
  margin-bottom: 48px;
}

.section-icon {
  font-size: 3rem;
  color: var(--gold);
  margin-bottom: 24px;
  display: block;
}

.main-description {
  font-size: 1.2rem;
  line-height: 1.7;
  margin: 0;
  opacity: 0.95;
}

.getting-started {
  margin-bottom: 48px;
}

.subsection-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 1.8rem;
  font-family: var(--font-heading);
  font-weight: 600;
  color: var(--gold);
  margin-bottom: 32px;
  text-align: center;
}

.subsection-title svg {
  font-size: 1.5rem;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.option-card {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.option-card:hover {
  border-color: rgba(255, 184, 28, 0.3);
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(255, 184, 28, 0.1);
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  border-radius: 12px;
  color: var(--navy-blue);
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(255, 184, 28, 0.3);
}

.option-content {
  flex: 1;
}

.option-content h4 {
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--light-gold);
  font-family: var(--font-heading);
}

.option-content p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  opacity: 0.9;
}

.contact-section {
  text-align: center;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 184, 28, 0.2);
}

.contact-text {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 32px;
  opacity: 0.9;
}

.cta-container {
  display: flex;
  justify-content: center;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--gold) 0%, var(--light-gold) 100%);
  color: var(--navy-blue);
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 24px rgba(255, 184, 28, 0.3);
  position: relative;
  overflow: hidden;
}

.cta-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s ease;
}

.cta-button:hover {
  background: linear-gradient(135deg, var(--light-gold) 0%, var(--gold) 100%);
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(255, 184, 28, 0.4);
}

.cta-button:hover::before {
  left: 100%;
}

/* Responsive Design */
@media (max-width: 768px) {
  .newcomers-section {
    padding: 60px 0;
  }
  
  .section-container {
    padding: 0 16px;
  }
  
  .section-header {
    margin-bottom: 48px;
  }
  
  .section-title {
    font-size: 2.2rem;
  }
  
  .section-subtitle {
    font-size: 1.1rem;
  }
  
  .content-card {
    padding: 32px 24px;
  }
  
  .intro-text {
    margin-bottom: 40px;
  }
  
  .section-icon {
    font-size: 2.5rem;
    margin-bottom: 20px;
  }
  
  .main-description {
    font-size: 1.1rem;
  }
  
  .subsection-title {
    font-size: 1.6rem;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .option-card {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .option-icon {
    align-self: center;
  }
  
  .getting-started {
    margin-bottom: 40px;
  }
}

@media (max-width: 480px) {
  .newcomers-section {
    padding: 40px 0;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .content-card {
    padding: 24px 16px;
  }
  
  .section-icon {
    font-size: 2rem;
  }
  
  .main-description {
    font-size: 1rem;
  }
  
  .cta-button {
    width: 100%;
    justify-content: center;
    padding: 18px 24px;
  }
}
</style>
